<template>
  <div class="flex flex-column gap-3">
    <a-modal
      title="Statistic"
      :visible="visibleStatisticModal"
      width="900px"
      @cancel="visibleStatisticModal = false"
      :destroy-on-close="true"
    >
      <profile-kpi :associate_number="associate.associate_number" />

      <template slot="footer">
        <div class="flex justify-content-between">
          <a-button @click="visibleStatisticModal = false"> Cancel </a-button>
        </div>
      </template>
    </a-modal>

    <filters-modal :visible.sync="visibleFiltersModal" :current-filters.sync="filters" />

    <rollback-modal :visible.sync="visibleRollbackModal" :id.sync="rollbackId" @rollback="loadData" />

    <create-modal :visible.sync="visibleCreateModal" @add="loadData" />

    <explanation-modal :visible.sync="visibleExplanationModal" />

    <div class="flex justify-content-between">
      <h3 class="m-0 flex gap-2 align-items-center">Rating</h3>
      <div class="flex gap-2 flex-wrap">
        <a-button
          v-if="
            hasUncontrolledPermission('manage_associates', {
              id: associate?.id
            })
          "
          class="flex gap-2 align-items-center"
          type="danger"
          @click="handleRecalibrate"
        >
          <a-icon icon="material-symbols:restart-alt" width="20" height="20" />
          Reset
        </a-button>
        <a-button class="flex gap-2 align-items-center" ghost @click="visibleStatisticModal = true">
          <a-icon icon="material-symbols:add-chart-outline" width="20" height="20" />
          View Statistic
        </a-button>
        <a-button
          class="flex align-items-center justify-content-center"
          @click="
            () => {
              reloadAssociate(true)
            }
          "
          title="Refresh Page"
        >
          <a-icon width="20" height="20" icon="solar:refresh-square-broken" />
        </a-button>
      </div>
    </div>

    <template>
      <div
        class="flex gap-2 align-items-center"
        v-if="associate?.point_system?.point_system_status?.is_show_points && associate.is_point_system_enabled"
      >
        <a-tooltip :title="associate.point_system.point_system_group.admin_title">
          <div
            class="rating-item"
            :style="{
              backgroundColor: `#${associate.point_system.point_system_group.color}`
            }"
          >
            {{ getShortcutNumber(associate.point_system.point_system_score) }}
          </div>
        </a-tooltip>
        <div class="flex flex-column gap-1">
          <span
            >Ranked #{{ associate.point_system.point_system_rank }} out of
            {{ associate.point_system.total_associate }}</span
          >
          <span>Best {{ associate.point_system.point_system_percent_rank }}% Performers</span>
        </div>
      </div>

      <a-descriptions :column="1" size="small" class="col-fixed p-0" :colon="false" v-else>
        <a-descriptions-item>
          <template slot="label">
            <div class="flex flex-column">
              <span>{{ associate?.point_system?.point_system_status?.title ?? '-' }}</span>
              <span :style="{ fontWeight: 'normal' }">
                {{ associate?.point_system?.point_system_status?.description ?? '-' }}
              </span>
            </div></template
          >
          <a-tooltip
            v-if="associate?.point_system?.point_system_status?.key === 'in_progress'"
            :title="`${associate?.point_system?.point_system_shift_count ?? 0} / ${associate?.point_system?.point_system_shift_required ?? 0} Shifts`"
          >
            <a-progress
              :style="{ width: '200px' }"
              :percent="
                Math.ceil((associate?.point_system?.point_system_shift_count ?? 0) * 100) /
                (associate?.point_system?.point_system_shift_required || 1)
              "
              :success-percent="
                Math.ceil((associate?.point_system?.point_system_shift_count ?? 0) * 100) /
                (associate?.point_system?.point_system_shift_required || 1)
              "
            />
          </a-tooltip>
        </a-descriptions-item>
        <a-descriptions-item label="Rating Activity">
          <a-switch
            :disabled="loadingActivated"
            :checked="associate.is_point_system_enabled"
            @change="handleActivated(!associate.is_point_system_enabled)"
        /></a-descriptions-item> </a-descriptions
    ></template>

    <a-spin :spinning="loadingActivated"
      ><div
        class="flex flex-column gap-3"
        v-if="
          (associate?.point_system?.point_system_status?.is_show_points ||
            associate?.point_system?.point_system_status?.key === 'in_progress') &&
          associate.is_point_system_enabled
        "
      >
        <statistic :statistic="statistic" :loading="loading" />

        <table-layout>
          <template #header>
            <h3 class="m-0 flex gap-2 align-items-center">History</h3>
          </template>

          <template #controls-left>
            <a-button
              v-if="
                hasUncontrolledPermission('manage_associates', {
                  id: associate?.id
                })
              "
              class="flex gap-2 align-items-center"
              type="primary"
              @click="visibleCreateModal = true"
            >
              <a-icon icon="material-symbols:add-box-outline" width="20" height="20" />
              Create Record
            </a-button>
            <a-button
              class="flex gap-2 align-items-center"
              type="primary"
              ghost
              @click="visibleExplanationModal = true"
            >
              <a-icon icon="mingcute:question-line" width="20" height="20" />
              How It Works
            </a-button>
          </template>

          <template #controls-right>
            <a-badge :count="countFilters">
              <a-button icon="filter" @click="visibleFiltersModal = true"> Filters </a-button>
            </a-badge>
            <a-button class="flex align-items-center justify-content-center" @click="loadData" title="Refresh Page">
              <a-icon width="20" height="20" icon="solar:refresh-square-broken" />
            </a-button>
          </template>

          <template #filters>
            <active-table-filters
              :items="activeFilterTags"
              @edit="openFilterEditor"
              @remove="removeFilter"
              @reset="resetFilters"
            />
          </template>

          <a-table
            size="small"
            :style="{ whiteSpace: 'wrap' }"
            :columns="columns"
            :data-source="tableData"
            :pagination="pagination"
            :scroll="{ y: 490, x: 1040 }"
            :rowKey="record => record.id"
            :rowClassName="getRowClassName"
            :loading="loading"
            @change="handleTableChange"
          >
            <span slot="associate" slot-scope="text, record">
              <grouped-associate-info
                v-if="record.manual_entered"
                :first-name="record.author.first_name"
                :last-name="record.author.last_name"
                :photo-url="record.author.photo_url"
                :associate-number="record.author.associate_number"
              />
              <span v-else>System</span>
            </span>
            <span slot="actions" slot-scope="text, record">
              <span
                @click="handleRollbackClick(record.id)"
                class="action-button"
                v-if="
                  record.allow_rollback &&
                  !record.rollback_action &&
                  hasUncontrolledPermission('manage_associates', {
                    id: associate?.id
                  })
                "
              >
                <a-icon width="24" height="24" icon="ic:round-cancel"
              /></span>
            </span>
          </a-table>
        </table-layout></div
    ></a-spin>
  </div>
</template>

<script>
import tableMixin from '@/mixins/tableMixin'
import util from '@/util'
import { Badge, Button, Descriptions, notification, Progress, Spin, Switch, Table, Tooltip } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import RatingRollbackModal from './rating-rollback-modal.vue'
import RatingCreateModal from './rating-create-modal.vue'
import ProfileViewKpi from '../profile-view-kpi.vue'
import RatingExplanationModal from './rating-explanation-modal.vue'
import RatingFiltersModal from './rating-filters-modal.vue'
import RatingStatistic from './rating-statistic.vue'
import GroupedAssociateInfo from '@/components/ui/grouped-associate-info.vue'
import permissionMixin from '@/mixins/permissionMixin'

const { mapState: profileState, mapActions: profileActions } = createNamespacedHelpers('profile')
const { mapActions: pointSystemActions, mapGetters: pointSystemGetters } = createNamespacedHelpers('pointSystem')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-table': Table,
    'a-button': Button,
    'a-badge': Badge,
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'a-tooltip': Tooltip,
    'a-progress': Progress,
    'a-switch': Switch,
    'a-spin': Spin,
    'profile-kpi': ProfileViewKpi,
    'rollback-modal': RatingRollbackModal,
    'create-modal': RatingCreateModal,
    'explanation-modal': RatingExplanationModal,
    'filters-modal': RatingFiltersModal,
    'grouped-associate-info': GroupedAssociateInfo,
    statistic: RatingStatistic
  },
  data() {
    return {
      loading: false,
      loadingActivated: false,

      tableData: [],
      statistic: undefined,

      filters: {},

      columns: [
        {
          title: 'Created At',
          key: 'created_at',
          width: 120,
          sorter: (a, b) => {
            const dateA = moment(a.created_at)
            const dateB = moment(b.created_at)
            return dateA - dateB
          },
          customRender: (_, record) => {
            return util.convertDateTimeToCurrentTimeZoneWithFormat(record.created_at, 'MM/DD/YYYY')
          }
        },
        {
          title: 'Author',
          key: 'author',
          width: 200,
          scopedSlots: { customRender: 'associate' },
          sorter: (a, b) => {
            const aName = util.combineAssociateName(a.first_name, a.last_name)
            const bName = util.combineAssociateName(b.first_name, b.last_name)

            return aName == null ? -1 : bName == null ? 1 : aName.localeCompare(bName)
          }
        },
        {
          title: 'Record',
          width: 400,
          key: 'description',
          customRender: (_, record) => {
            return `${record.action_type.type_name}: ${record.reason ?? record.description}`
          },
          sorter: (a, b) =>
            a.description == null ? -1 : b.description == null ? 1 : a.description.localeCompare(b.description)
        },
        {
          title: 'Points',
          width: 100,
          key: 'value',
          customRender: (_, record) => {
            let value = record.value

            if (record.action_type.is_negative) {
              value = value * -1
            }

            if (record.rollback_action) {
              value = value * -1
            }

            const content = (
              <span
                style={{
                  color: `${value < 0 ? 'var(--error-color)' : 'var(--success-color)'}`
                }}
              >
                {value < 0 ? '-' : '+'} {Math.abs(value)}
              </span>
            )

            return content
          },
          sorter: (a, b) => {
            const getDisplayedValue = record => {
              let value = record.value

              if (record.action_type?.is_negative) {
                value *= -1
              }

              if (record.rollback_action) {
                value *= -1
              }

              return value
            }

            return getDisplayedValue(a) - getDisplayedValue(b)
          }
        },
        {
          title: 'Source',
          width: 120,
          key: 'source',
          customRender: (_, record) => {
            if (record.manual_entered && !!record.rollback_action) {
              return 'Cancel'
            }
            if (record.manual_entered) {
              return 'Manual'
            }
            return 'Event'
          },
          sorter: (a, b) => {
            const getValue = record => {
              if (record.manual_entered && record.rollback_action) {
                return 'Cancel'
              }
              if (record.manual_entered) return 'Manual'
              return 'Event'
            }

            const order = { Cancel: 0, Manual: 1, Event: 2 }
            return order[getValue(a)] - order[getValue(b)]
          }
        },

        {
          title: 'Actions',
          key: 'actions',
          width: 100,
          scopedSlots: { customRender: 'actions' }
        }
      ],

      visibleRollbackModal: false,
      visibleCreateModal: false,
      visibleStatisticModal: false,
      visibleExplanationModal: false,
      visibleFiltersModal: false,
      rollbackId: undefined
    }
  },
  mixins: [tableMixin, permissionMixin],
  watch: {
    filters: {
      handler(newVal, prevVal) {
        if (JSON.stringify(newVal) != JSON.stringify(prevVal)) {
          this.pagination.current = 1
          this.loadData()
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),
    ...pointSystemGetters(['getTypes', 'getStatus', 'getGroups']),

    tableFilterEmptyValues() {
      return {
        types: [],
        sub_types: []
      }
    }
  },
  methods: {
    ...pointSystemActions(['getAction', 'postRecalibration', 'getStatistic']),
    ...profileActions(['updateAssociate', 'setAssociate', 'reloadAssociate']),
    ...modalActions(['openModal']),

    getShortcutNumber: util.getShortcutNumber,

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    handleRollbackClick(id) {
      this.visibleRollbackModal = true
      this.rollbackId = id
    },

    handleTableChange(pagination) {
      const isPaginationChanged =
        pagination.current !== this.pagination.current || pagination.pageSize !== this.pagination.pageSize

      if (!isPaginationChanged) {
        return
      }

      this.pagination = { ...pagination }
      this.loadData()
    },

    async loadData() {
      if (!this.associate?.id) {
        return
      }

      this.loading = true

      let skipFinally = false

      try {
        const isShowPoints = this.associate?.point_system?.point_system_status?.is_show_points
        const payload = {
          associate_id: this.associate.id,
          ...this.filters,
          types: !isShowPoints ? ['other'] : this.filters?.types
        }

        const [data, statistic] = await Promise.all([
          this.getAction({
            ...payload,
            page: this.pagination.current,
            size: this.pagination.pageSize
          }),
          this.getStatistic(payload)
        ])

        this.statistic = statistic

        this.tableData = data.items
        this.pagination.total = data.total_count
      } catch (error) {
        if (error === 'canceled') {
          skipFinally = true
          this.loading = true
          return
        }

        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        if (!skipFinally) this.loading = false
      }
    },

    async handleRecalibrate() {
      this.openModal({
        type: 'deletion',
        data: {
          visible: true,
          title: 'Reset Rating for Associate?',
          description: 'Are you sure you want to reset rating?',
          action: 'reset',
          onConfirm: async () => {
            try {
              const payload = {
                associate_id: this.associate.id
              }
              await this.postRecalibration(payload)
            } catch (error) {
              this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
            }
          }
        }
      })
    },

    async handleActivated(is_point_system_enabled) {
      this.loadingActivated = true

      try {
        const payload = {
          associate: { ...this.associate, is_point_system_enabled }
        }

        await this.updateAssociate(payload)

        this.setAssociate({ ...this.associate, is_point_system_enabled })
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        this.loadingActivated = false
      }
    }
  }
}
</script>

<style scoped>
.rating-item {
  width: fit-content;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  border-radius: 8px;
  color: #232326;
}
</style>
